import {
  cleanImageResourceUrl,
  downloadFileFromUrl,
  drawFileSvg,
  getURLParametersByKey,
} from '@frontend/utils';
import { FILE_TYPE } from '@frontend/constants/file-image-types';

const SKIPPED_UPLOAD_NAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini']);

function normalizeRelativePath(path) {
  return String(path || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
}

function shouldSkipUpload(file, relativePath = '') {
  const path = normalizeRelativePath(relativePath || file.webkitRelativePath || file.name || '');
  if (!path || path.endsWith('/')) {
    return true;
  }
  const baseName = path.split('/').pop() || '';
  if (!baseName || SKIPPED_UPLOAD_NAMES.has(baseName.toLowerCase())) {
    return true;
  }
  if (!baseName.includes('.') && file.size === 0) {
    return true;
  }
  return false;
}

function stripCommonRootPrefix(entries) {
  if (!entries.length) {
    return entries;
  }
  const paths = entries
    .map((entry) => normalizeRelativePath(entry.relativePath))
    .filter(Boolean);
  if (!paths.length) {
    return entries;
  }
  const roots = paths.map((path) => path.split('/')[0]);
  const commonRoot = roots.every((root) => root === roots[0]) ? roots[0] : null;
  if (!commonRoot) {
    return entries;
  }
  const prefix = `${commonRoot}/`;
  return entries.map((entry) => {
    const relativePath = normalizeRelativePath(entry.relativePath);
    if (!relativePath.startsWith(prefix)) {
      return entry;
    }
    return {
      ...entry,
      relativePath: relativePath.slice(prefix.length),
    };
  });
}

function readAllDirectoryEntries(reader) {
  return new Promise((resolve, reject) => {
    const entries = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (!batch.length) {
          resolve(entries);
          return;
        }
        entries.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

async function traverseFileTreeEntry(entry, path = '') {
  if (!entry) {
    return [];
  }
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => {
      entry.file(resolve, reject);
    });
    const relativePath = normalizeRelativePath(`${path}${file.name}`);
    if (shouldSkipUpload(file, relativePath)) {
      return [];
    }
    return [{ file, relativePath }];
  }
  if (!entry.isDirectory) {
    return [];
  }
  const reader = entry.createReader();
  const children = await readAllDirectoryEntries(reader);
  const nested = await Promise.all(
    children.map((child) => traverseFileTreeEntry(child, `${path}${entry.name}/`)),
  );
  return nested.flat();
}

// Dragged files stay hidden until drop; 'Files' in types also excludes Sortable drags.
export const dragCarriesFiles = event =>
  Array.from(event.dataTransfer?.types || []).includes('Files');

export async function collectFilesFromDrop(event) {
  const dataTransfer = event && event.dataTransfer;
  if (!dataTransfer) {
    return [];
  }

  const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];
  const hasEntries = items.some((item) => typeof item.webkitGetAsEntry === 'function');
  if (hasEntries) {
    const nested = await Promise.all(
      items
        .filter((item) => item.kind === 'file' && typeof item.webkitGetAsEntry === 'function')
        .map((item) => traverseFileTreeEntry(item.webkitGetAsEntry())),
    );
    const entries = stripCommonRootPrefix(nested.flat());
    if (entries.length) {
      return entries;
    }
  }

  const files = dataTransfer.files ? Array.from(dataTransfer.files) : [];
  const entries = files
    .map((file) => ({
      file,
      relativePath: normalizeRelativePath(file.webkitRelativePath || file.name),
    }))
    .filter((entry) => !shouldSkipUpload(entry.file, entry.relativePath));

  return stripCommonRootPrefix(entries);
}

export function collectFilesFromInput(fileList) {
  const files = fileList ? Array.from(fileList) : [];
  const entries = files
    .map((file) => ({
      file,
      relativePath: normalizeRelativePath(file.webkitRelativePath || file.name),
    }))
    .filter((entry) => !shouldSkipUpload(entry.file, entry.relativePath));

  return stripCommonRootPrefix(entries);
}

export function pendingAttachmentLabel(file, relativePath = '') {
  const path = normalizeRelativePath(relativePath || file.webkitRelativePath || file.name || '');
  if (path.includes('/')) {
    return path;
  }
  return file.name || path || 'file';
}

export function newPendingAttachment(file, relativePath = '') {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const mime = file.type || '';
  const normalizedPath = normalizeRelativePath(relativePath || file.webkitRelativePath || '');
  const kind = isRenderableImageFile(file)
    ? 'image'
    : isVideoFile(file)
      ? 'video'
      : 'file';
  let previewUrl = null;
  try {
    previewUrl = objectUrlForFile(file);
  } catch {
    previewUrl = null;
  }

  return {
    id,
    name: pendingAttachmentLabel(file, normalizedPath),
    file,
    relativePath: normalizedPath,
    previewUrl,
    kind,
    mime,
  };
}

export function revokePendingPreview(item) {
  if (item && item.previewUrl) {
    try {
      URL.revokeObjectURL(item.previewUrl);
    } catch {
      /* ignore */
    }
  }
}

// Keep in step with UploadTypeGuard::NON_RENDERABLE_IMAGES on the server.
export const NON_RENDERABLE_IMAGE_EXTENSIONS = new Set(['psd', 'psb', 'tif', 'tiff', 'heic', 'heif']);
export const NON_RENDERABLE_IMAGE_MIMES = new Set([
  'image/vnd.adobe.photoshop',
  'application/photoshop',
  'image/tiff',
  'image/x-tiff',
  'image/heic',
  'image/heif',
]);

const EXT_TO_FILE_TYPE = {
  psd: FILE_TYPE.PSD,
  psb: FILE_TYPE.PSB,
  pdf: FILE_TYPE.PDF,
  doc: FILE_TYPE.DOC,
  docx: FILE_TYPE.DOC,
  xls: FILE_TYPE.XLS,
  xlsx: FILE_TYPE.XLS,
  zip: FILE_TYPE.ZIP,
  rar: FILE_TYPE.ZIP,
  ppt: FILE_TYPE.PPT,
  pptx: FILE_TYPE.PPTX,
  mp4: FILE_TYPE.MP4,
  mov: FILE_TYPE.MOV,
  avi: FILE_TYPE.AVI,
  webm: FILE_TYPE.WEBM,
  mp3: FILE_TYPE.MP3,
  gif: FILE_TYPE.GIF,
  svg: FILE_TYPE.SVG,
  tiff: FILE_TYPE.TIFF,
  tif: FILE_TYPE.TIF,
  ai: FILE_TYPE.AI,
  eps: FILE_TYPE.EPS,
  csv: FILE_TYPE.CSV,
};

export function referenceExtension(ref) {
  const name = ((ref && ref.name) || '').trim();
  const idx = name.lastIndexOf('.');
  if (idx <= 0) {
    return '';
  }
  return name.slice(idx + 1).toLowerCase();
}

export function referenceFileType(ref) {
  return EXT_TO_FILE_TYPE[referenceExtension(ref)] || null;
}

export function referenceFileIconMarkup(ref) {
  const type = referenceFileType(ref);
  return drawFileSvg({
    type: type || 'file',
    name: (ref && ref.name) || '',
  });
}

function isInlineDataUrl(url) {
  return url.startsWith('blob:') || url.startsWith('data:');
}

function isStoredFileUrl(url) {
  return !!url && typeof url === 'string' && !isInlineDataUrl(url);
}

// Embeds can carry ?lightweight/scale params — a download must be the original file.
function downloadableUrl(url) {
  if (!isStoredFileUrl(url)) {
    return url || '';
  }
  const path = cleanImageResourceUrl(url);
  return path.startsWith('/') ? `${window.location.origin}${path}` : path;
}

// Distinct from utils' getFileNameFromURL, which drops the extension and ignores /share?file=.
export function fullFileNameFromUrl(url) {
  if (!isStoredFileUrl(url)) {
    return '';
  }
  try {
    const fileParam = getURLParametersByKey(url, 'file');
    const path = fileParam || decodeURIComponent(cleanImageResourceUrl(url).split('?')[0]);
    return (path.split('/').pop() || '').trim();
  } catch {
    return '';
  }
}

// Server URLs come from siteUrl(), which is not always the host a proof was shared on.
export function sameOriginUrl(url) {
  if (!url) {
    return '';
  }
  try {
    return `${window.location.origin}${new URL(url, window.location.origin).pathname}`;
  } catch {
    return url;
  }
}

export function downloadUrlAsFile(url, filename) {
  const href = downloadableUrl(url);
  if (!href) {
    return Promise.resolve(false);
  }
  // Must go through a blob: /share answers with Content-Disposition, which beats `download`
  // and would save the image_cache hash as the filename.
  return downloadFileFromUrl(href, (filename || '').trim() || 'download');
}

export function referenceChipIconClass(ref) {
  const kind = ref && ref.kind;
  if (kind === 'video') {
    return 'bi-camera-video';
  }
  if (kind === 'image') {
    return 'bi-image';
  }
  return 'bi-file-earmark';
}

export function isImageFile(file) {
  return !!file && (file.type || '').startsWith('image/');
}

export function isRenderableImageFile(file) {
  if (!isImageFile(file)) {
    return false;
  }
  if (NON_RENDERABLE_IMAGE_MIMES.has((file.type || '').toLowerCase())) {
    return false;
  }

  return !NON_RENDERABLE_IMAGE_EXTENSIONS.has(referenceExtension(file));
}

// Browsers (notably on Windows) often report an empty MIME for .mov; keys mirror the backend's
// accepted video extensions, and the value re-supplies the type so blob URLs still play.
const VIDEO_EXTENSION_MIME = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
};

export function isVideoFile(file) {
  if (!file) {
    return false;
  }
  if ((file.type || '').startsWith('video/')) {
    return true;
  }
  return Object.prototype.hasOwnProperty.call(VIDEO_EXTENSION_MIME, referenceExtension(file));
}

// Re-type empty-MIME videos (e.g. .mov) so the blob URL opens as a playable video, not raw bytes.
export function objectUrlForFile(file) {
  const fallbackMime = file.type ? '' : (VIDEO_EXTENSION_MIME[referenceExtension(file)] || '');
  const source = fallbackMime ? new Blob([file], { type: fallbackMime }) : file;
  return URL.createObjectURL(source);
}